import { buildCostReportAnalyzePrompt } from 'features/cost-report/utils/buildCostReportAnalyzePrompt';
import { useCostReportTimeRangeSelection } from 'features/cost-report/hooks/useCostReportTimeRangeSelection';

import { ChartMoney } from './_components/ChartMoney';
import { ChartTitle } from './_components/ChartTitle';
import { makeChartTooltip } from './_components/ChartTooltip';
import { CostPerProvisionedResourceChart } from './_components/CostPerProvisionedResourceChart';
import {
  DataPointMapper,
  useNormalizedResourceCostChartData,
} from './_hooks/useNormalizedResourceCostChartData';

import { useCluster } from '@cast/shared-cluster-context-data-access';

const chartDataMapper: DataPointMapper = ({
  normalizedCostPerGpu,
  onDemandCostPerGpu,
  spotCostPerGpu,
  fallbackCostPerGpu,
}) => ({
  normalizedCost: normalizedCostPerGpu,
  onDemandCost: onDemandCostPerGpu,
  spotCost: spotCostPerGpu,
  fallbackCost: fallbackCostPerGpu,
});

type Props = { costType: string };

export const GpuCostChart = ({ costType }: Props) => {
  const { cluster } = useCluster();
  const { getRange } = useCostReportTimeRangeSelection();
  const { isLoading, data } =
    useNormalizedResourceCostChartData(chartDataMapper);
  const { tooltipHeader, tooltipFooter } = makeChartTooltip({ unit: 'GPU' });

  return (
    <CostPerProvisionedResourceChart
      header={
        <ChartTitle
          title={`${costType} GPU COST`}
          infoTooltip="Total daily GPU spend divided by the provisioned GPU"
          cost={
            <ChartMoney mapper={({ hourlyCostPerGpu }) => hourlyCostPerGpu} />
          }
        />
      }
      tooltipHeader={tooltipHeader}
      tooltipFooter={tooltipFooter}
      data={data}
      loading={isLoading}
      testId="cost-per-provisioned-resource-chart-gpu"
      buildPrompt={buildCostReportAnalyzePrompt({
        scopeName: cluster?.name,
        context: 'GPU cost per provisioned resource',
        getDefaultRange: getRange,
      })}
    />
  );
};
